import { useEffect, useRef, useState } from 'react';

import { Button } from '@/components/ui/Button';

/**
 * The camera, pointed at a pack.
 *
 * ── WHY SCANNING IS THE WHOLE FEATURE ───────────────────────────────────────
 *
 * A pharmacist listing stock is standing at a counter holding a box. Typing a
 * thirteen-digit number off that box, then a batch number, then an expiry, is
 * the friction that makes people list twenty products and stop. Pointing a
 * camera at it is two seconds and cannot be mistyped.
 *
 * ── WHY THERE IS NO LIBRARY HERE ────────────────────────────────────────────
 *
 * Browsers decode barcodes natively now. Adding a decoder would be roughly a
 * megabyte of JavaScript shipped to every pharmacist on a Nigerian mobile
 * connection, most of whom are on Android Chrome where the native one works.
 *
 * Where it is missing — iOS Safari today — this says so plainly and offers the
 * keyboard instead. An honest fallback beats a spinner that never resolves.
 */

interface DetectedBarcode {
  rawValue: string;
}

interface BarcodeDetectorLike {
  detect(source: CanvasImageSource): Promise<DetectedBarcode[]>;
}

declare global {
  interface Window {
    BarcodeDetector?: new (options?: { formats?: string[] }) => BarcodeDetectorLike;
  }
}

type State = 'starting' | 'scanning' | 'unsupported' | 'denied';

/** Whether this browser can decode a barcode from a camera at all. */
function canScan(): boolean {
  return Boolean(window.BarcodeDetector) && Boolean(navigator.mediaDevices);
}

export function BarcodeScanner({
  onDetected,
  onCancel,
}: {
  onDetected: (code: string) => void;
  onCancel: () => void;
}) {
  const video = useRef<HTMLVideoElement>(null);
  const [typed, setTyped] = useState('');

  // Decided once, at first render: whether this browser can do it at all is a
  // fact about the browser, not something to discover in an effect.
  const [state, setState] = useState<State>(() => (canScan() ? 'starting' : 'unsupported'));

  // Held in a ref so the camera is not torn down and restarted on every render.
  // The caller passes an inline arrow, which is a new function each time.
  const detected = useRef(onDetected);

  useEffect(() => {
    detected.current = onDetected;
  }, [onDetected]);

  useEffect(() => {
    if (!canScan()) {
      return;
    }

    let stream: MediaStream | null = null;
    let timer: number | undefined;
    let stopped = false;

    // EAN and UPC cover every medicine carton sold in Nigeria; DataMatrix is
    // the 2D code that also carries batch and expiry where a pack has one.
    const detector = new window.BarcodeDetector!({
      formats: ['ean_13', 'ean_8', 'upc_a', 'upc_e', 'code_128', 'data_matrix'],
    });

    navigator.mediaDevices
      .getUserMedia({ video: { facingMode: 'environment' } })
      .then((granted) => {
        if (stopped) {
          granted.getTracks().forEach((track) => track.stop());

          return;
        }

        stream = granted;

        if (video.current) {
          video.current.srcObject = granted;
          void video.current.play();
        }

        setState('scanning');

        // Polled rather than run every frame: decoding is the expensive part,
        // and three times a second is faster than anyone can move a box.
        timer = window.setInterval(async () => {
          if (!video.current || video.current.readyState < 2) return;

          try {
            const [found] = await detector.detect(video.current);

            if (found?.rawValue) {
              detected.current(found.rawValue);
            }
          } catch {
            // A single failed frame is not worth telling anybody about.
          }
        }, 300);
      })
      .catch(() => setState('denied'));

    return () => {
      stopped = true;
      window.clearInterval(timer);
      stream?.getTracks().forEach((track) => track.stop());
    };
  }, []);

  if (state === 'unsupported' || state === 'denied') {
    return (
      <div className="rounded-card border border-line bg-white p-4">
        <p className="m-0 font-mono text-[10px] uppercase tracking-[0.14em] text-faint">
          {state === 'denied' ? 'Camera blocked' : 'No camera scanning here'}
        </p>
        <p className="m-0 mt-1.5 text-[13.5px] leading-relaxed text-ink">
          {state === 'denied'
            ? 'Your browser blocked the camera. Allow it in the address bar, or type the number under the barcode.'
            : 'This browser cannot scan barcodes — iPhones mostly cannot yet. Type the number printed under the barcode instead.'}
        </p>

        <form
          className="mt-3 flex flex-wrap gap-2"
          onSubmit={(event) => {
            event.preventDefault();
            if (typed.trim()) onDetected(typed.trim());
          }}
        >
          <input
            value={typed}
            onChange={(event) => setTyped(event.target.value)}
            inputMode="numeric"
            autoFocus
            placeholder="5901234123457"
            className="min-h-11 min-w-0 flex-1 rounded-btn border border-line bg-white px-3 font-mono text-[14px] tracking-[0.04em] text-ink outline-none focus:border-blue"
          />
          <Button type="submit" disabled={!typed.trim()}>
            Find it
          </Button>
        </form>

        <button
          type="button"
          onClick={onCancel}
          className="mt-3 min-h-11 text-[12.5px] font-bold text-muted"
        >
          Cancel
        </button>
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-card border border-line bg-ink">
      <div className="relative aspect-[4/3] w-full">
        <video
          ref={video}
          muted
          playsInline
          data-testid="scanner-video"
          className="h-full w-full object-cover"
        />

        {/* A window to aim through. Pharmacists hold a box at arm's length and
            need to know where the camera is actually looking. */}
        <div className="pointer-events-none absolute inset-0 grid place-items-center">
          <div className="h-[28%] w-[78%] rounded-[10px] border-2 border-white/90 shadow-[0_0_0_2000px_rgba(8,19,43,0.45)]" />
        </div>

        <p className="absolute inset-x-0 bottom-0 m-0 bg-ink/70 px-4 py-2.5 text-center text-[12.5px] text-white">
          {state === 'starting' ? 'Starting the camera…' : 'Point at the barcode on the pack'}
        </p>
      </div>

      <button
        type="button"
        onClick={onCancel}
        className="min-h-12 w-full bg-white text-[13px] font-bold text-ink"
      >
        Cancel
      </button>
    </div>
  );
}
